<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { theme, toggleTheme } from '@/lib/theme';
import IconAction from '@/components/IconAction.vue';

/**
 * En haut à droite : avatar et nom du compte connecté, bascule clair/sombre,
 * puis l'engrenage des préférences. `compact` (barre mobile) masque le nom.
 */
defineProps<{ compact?: boolean }>();

const auth = useAuthStore();

const displayName = computed(() => {
  const user = auth.user;
  if (!user) return '';
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.email;
});

/** Initiales du prénom et du nom, à défaut les deux premières lettres de l'email. */
const initials = computed(() => {
  const user = auth.user;
  if (!user) return '';
  const fromName = [user.firstName, user.lastName]
    .map((part) => part?.trim()[0] ?? '')
    .join('');
  return (fromName || user.email.slice(0, 2)).toUpperCase();
});
</script>

<template>
  <div v-if="auth.user" class="flex items-center gap-2">
    <div class="flex items-center gap-2" :title="`Connecté : ${auth.user.email}`">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pulse-500 text-sm font-semibold text-pulse-900"
        role="img"
        :aria-label="`Connecté en tant que ${displayName}`"
      >
        {{ initials }}
      </span>
      <span v-if="!compact" class="hidden max-w-[12rem] truncate text-sm font-medium text-ink-soft md:inline">
        {{ displayName }}
      </span>
    </div>
    <IconAction
      :icon="theme === 'dark' ? 'sun' : 'moon'"
      :label="theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'"
      tooltip="bottom"
      @click="toggleTheme"
    />
    <IconAction icon="help" label="Centre d'aide" to="/aide" tooltip="bottom" />
    <IconAction icon="preferences" label="Préférences" to="/preferences" tooltip="bottom" />
  </div>
</template>
